import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };
  public props: Props;

  constructor(props: Props) {
    super(props);
    this.props = props;
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // If it's a dynamic module import failure, auto-reload once to fetch fresh assets
    const msg = error?.message || '';
    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('dynamically imported')
    ) {
      const reloaded = sessionStorage.getItem('chunk_reload_error_boundary');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload_error_boundary', 'true');
        window.location.reload();
      }
    }
  }

  private handleReload = () => {
    sessionStorage.removeItem('chunk_reload_error_boundary');
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || '';
      const isDynamicImportError =
        msg.includes('dynamically imported module') ||
        msg.includes('Loading chunk') ||
        msg.includes('Failed to fetch');

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-lg">
            <div className="w-12 h-12 bg-red-50 text-[#E2231A] rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-2">
              {isDynamicImportError ? 'Actualizare de versiune disponibilă' : 'A apărut o problemă la afișare'}
            </h2>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              {isDynamicImportError
                ? 'Aplicația a fost actualizată pe server. Te rugăm să reîmprospătezi pagina pentru a încărca cea mai recentă versiune.'
                : 'A apărut o eroare temporară la încărcarea modulului. Poți reîncărca pagina sau reveni la pagina principală.'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 bg-[#E2231A] text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-[#C41A12] transition-colors cursor-pointer shadow-xs"
              >
                <RefreshCw className="w-4 h-4" />
                Reîncarcă pagina
              </button>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 bg-slate-100 text-slate-700 px-4 py-2.5 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
              >
                <Home className="w-4 h-4" />
                Acasă
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
